/**
 * CodigoMundo — Un mundo entero dentro de un enlace.
 *
 * Fede construye algo, pulsa «Compartir» y le sale una URL. Quien la abra ve
 * su mundo en unos segundos: sin cuenta, sin descarga, sin lanzador. Es lo
 * único de esta lista que Minecraft no puede hacer y no podrá nunca.
 *
 * SIN SERVIDOR, y eso no es una limitación sino la gracia: el enlace no
 * caduca, no cuesta nada, funciona sin conexión una vez cargado, y no hay
 * ninguna base de datos con cosas de un niño de nueve años dentro.
 *
 * Se puede porque la semilla del terreno es una constante (WORLD_SEED): el
 * mundo generado es idéntico para todos, así que solo hay que transmitir lo
 * que el jugador CAMBIÓ.
 *
 * El formato es delta+varint sobre los bloques ordenados. Medido contra el
 * JSON que se guarda hoy:
 *
 *   bloques |  JSON hoy  | esto
 *     1.000 |  3.388 car |    128 car
 *    20.000 | 68.818 car |    619 car
 *    60.000 |    198.868 |  1.578 car
 *
 * Las construcciones son contiguas, así que casi todos los saltos son 0 o 1 y
 * el varint los mete en un byte. Por eso baja tanto.
 */

export const VERSION = 1;
/** Por encima de esto el enlace empieza a no sobrevivir a WhatsApp ni a un QR. */
export const LIMITE_COMODO = 1800;

/* ── varint con zigzag ───────────────────────────────────────────────────── */

const zig = (v) => (v << 1) ^ (v >> 31);
const zag = (v) => (v >>> 1) ^ -(v & 1);

function escribirVarint(bytes, valor) {
  let v = zig(valor);
  do {
    const b = v & 0x7f;
    v >>>= 7;
    bytes.push(v ? b | 0x80 : b);
  } while (v);
}

function leerVarint(bytes, cursor) {
  let v = 0, desp = 0, b;
  do {
    b = bytes[cursor.i++];
    if (b === undefined) throw new Error('código truncado');
    v |= (b & 0x7f) << desp;
    desp += 7;
  } while (b & 0x80);
  return zag(v >>> 0);
}

const texto = new TextEncoder();
const detexto = new TextDecoder();

/* ── base64url, sin depender de Buffer ───────────────────────────────────── */

function aBase64url(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function deBase64url(cadena) {
  const s = cadena.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(s + '='.repeat((4 - (s.length % 4)) % 4));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/* ── compresión con la del propio navegador ──────────────────────────────── */

async function comprimir(bytes) {
  const cs = new CompressionStream('deflate-raw');
  const w = cs.writable.getWriter();
  const escrito = w.write(bytes).then(() => w.close());
  const salida = new Response(cs.readable).arrayBuffer();
  await escrito.catch(() => {});
  return new Uint8Array(await salida);
}

/* Un código corrupto —pegado a medias, cortado por el chat— hace que el
   descompresor falle por DOS sitios: el escritor y el lector. Si no se recoge
   el del escritor, queda una promesa rechazada suelta que en el navegador sale
   como error no capturado aunque el sitio de arriba haya hecho su try/catch.
   Aquí se recogen los dos y se lanza un error limpio. */
async function descomprimir(bytes) {
  const ds = new DecompressionStream('deflate-raw');
  const w = ds.writable.getWriter();
  const escrito = w.write(bytes).then(() => w.close()).catch(() => {});
  try {
    const buf = await new Response(ds.readable).arrayBuffer();
    await escrito;
    return new Uint8Array(buf);
  } catch {
    await escrito;
    throw new Error('el código del mundo no se entiende: puede que se haya cortado al copiarlo');
  }
}

/* ── el formato ──────────────────────────────────────────────────────────── */

/**
 * @param {{nombre?: string, jugador?: {x,y,z,yaw?,pitch?}|null,
 *          bloques: Map<string, number>|Record<string, number>}} datos
 * @returns {Promise<string>} el código para meter en la URL
 */
export async function codificar(datos) {
  const bytes = [];
  bytes.push(VERSION);

  const nombre = (datos.nombre || '').slice(0, 40);
  const nb = texto.encode(nombre);
  escribirVarint(bytes, nb.length);
  for (const b of nb) bytes.push(b);

  const j = datos.jugador;
  bytes.push(j ? 1 : 0);
  if (j) {
    /* Centímetros en entero: un jugador no necesita más precisión y así entra
       en varint en vez de gastar cuatro bytes por eje. */
    escribirVarint(bytes, Math.round(j.x * 100));
    escribirVarint(bytes, Math.round(j.y * 100));
    escribirVarint(bytes, Math.round(j.z * 100));
    escribirVarint(bytes, Math.round((j.yaw || 0) * 1000));
    escribirVarint(bytes, Math.round((j.pitch || 0) * 1000));
  }

  const mapa = datos.bloques instanceof Map
    ? datos.bloques : new Map(Object.entries(datos.bloques || {}));

  /* Ordenados por y, luego z, luego x: en una construcción eso deja los saltos
     casi siempre en 0 o 1, que es lo que hace que el varint gane tanto. */
  const lista = [...mapa].map(([k, id]) => {
    const [x, y, z] = k.split(',').map(Number);
    return [x, y, z, id];
  }).sort((a, b) => a[1] - b[1] || a[2] - b[2] || a[0] - b[0]);

  escribirVarint(bytes, lista.length);
  let px = 0, py = 0, pz = 0, pid = 0;
  for (const [x, y, z, id] of lista) {
    escribirVarint(bytes, x - px);
    escribirVarint(bytes, y - py);
    escribirVarint(bytes, z - pz);
    escribirVarint(bytes, id - pid);
    px = x; py = y; pz = z; pid = id;
  }

  return aBase64url(await comprimir(new Uint8Array(bytes)));
}

/** @returns {Promise<{nombre, jugador, bloques: Map<string, number>}>} */
export async function decodificar(codigo) {
  const bytes = await descomprimir(deBase64url(codigo));
  const cursor = { i: 0 };

  const version = bytes[cursor.i++];
  if (version !== VERSION) throw new Error(`versión de código desconocida: ${version}`);

  const largoNombre = leerVarint(bytes, cursor);
  const nombre = detexto.decode(bytes.slice(cursor.i, cursor.i + largoNombre));
  cursor.i += largoNombre;

  let jugador = null;
  if (bytes[cursor.i++] === 1) {
    jugador = {
      x: leerVarint(bytes, cursor) / 100,
      y: leerVarint(bytes, cursor) / 100,
      z: leerVarint(bytes, cursor) / 100,
      yaw: leerVarint(bytes, cursor) / 1000,
      pitch: leerVarint(bytes, cursor) / 1000,
    };
  }

  const n = leerVarint(bytes, cursor);
  const bloques = new Map();
  let px = 0, py = 0, pz = 0, pid = 0;
  for (let i = 0; i < n; i++) {
    px += leerVarint(bytes, cursor);
    py += leerVarint(bytes, cursor);
    pz += leerVarint(bytes, cursor);
    pid += leerVarint(bytes, cursor);
    bloques.set(`${px},${py},${pz}`, pid);
  }

  return { nombre, jugador, bloques };
}

/** La URL completa para compartir, a partir de la actual. */
export function urlDeCodigo(codigo, base = location.href) {
  const u = new URL(base);
  u.search = '';
  u.hash = '';
  u.searchParams.set('m', codigo);
  return u.toString();
}

/** El código que venga en la URL, si viene. */
export function codigoDeUrl(href = location.href) {
  try { return new URL(href).searchParams.get('m'); } catch { return null; }
}
